/**
 * Noctua embed loader.
 *
 * This is the only script customers load on their pages, so it stays tiny and
 * dependency-free. It exposes the public `window.Noctua` API and, when a
 * feedback session starts, overlays a full-viewport iframe pointing at the
 * hosted widget app (deployed alongside this file under /widget/). All widget
 * code, styling, and third-party dependencies live inside that iframe: nothing
 * but this loader executes on the host page, and the host page's CSP never
 * applies to the widget document.
 *
 * Handshake: the widget posts `noctua:ready` once its script runs; the loader
 * replies with a single `noctua:init` carrying config + context. The widget
 * posts `noctua:close` when the conversation UI should go away. Both sides
 * verify the counterparty (source window + origin) on every message.
 *
 * Besides the on-demand conversation overlay, the loader can host the
 * Noctua-provided launcher button: a small always-present iframe anchored to a
 * viewport corner (deployed alongside this file under /launcher/). Whether it
 * shows — and how it looks — is channel config the launcher document fetches
 * itself from the public widget config API, so the host page still makes no
 * API calls and appearance changes need no embed change. The loader only
 * handles its `noctua:launcher-ready`/`noctua:launcher-size` handshake and
 * opens the conversation on `noctua:open`. Integrators who never enable the
 * launcher (or set noctuaSettings.hideLauncher) keep the manual
 * startFeedback() flow unchanged.
 */

const STORAGE_KEY = 'noctua-user-context'
const GROUP_STORAGE_KEY = 'noctua-group-context'
const IFRAME_ID = 'noctua-widget-frame'
const LAUNCHER_IFRAME_ID = 'noctua-launcher-frame'

// The widget app is deployed next to the loader, so its URL derives from the
// loader's own script URL. document.currentScript is captured now, at
// evaluation time — it is null again once execution ends. It is also null for
// module scripts (dev server) and in tests; both fall back to the page base,
// and the workbench overrides via noctuaSettings.widgetUrl anyway.
const loaderSrc = document.currentScript?.src
const DEFAULT_WIDGET_URL = new URL('widget/', loaderSrc || document.baseURI).href
const DEFAULT_LAUNCHER_URL = new URL('launcher/', loaderSrc || document.baseURI).href

// Optional build-time default so integrators only have to supply a publicKey.
const DEFAULT_API_BASE = import.meta.env.VITE_API_BASE || null

let iframe = null
let widgetOrigin = null
let initPayload = null
let initialized = false
let escapeHandler = null
let scrollState = null
let messageListenerBound = false
let launcherFrame = null
let launcherOrigin = null
let launcherInitialized = false

/**
 * Persist visitor identity on the host origin so every future session carries
 * it. Merged into the context sent to the widget; see buildContext() for
 * precedence.
 */
function identify(context) {
  if (!context || typeof context !== 'object' || Array.isArray(context)) return
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(context))
  } catch {
    // Storage unavailable (private mode, blocked) — identity just won't persist.
  }
  // The capture bundle (if loaded) mirrors identity into telemetry.
  try {
    window.dispatchEvent(new CustomEvent('noctua:identify', { detail: context }))
  } catch {
    // CustomEvent unavailable — capture just misses this identify.
  }
}

/**
 * Declare the account/workspace the visitor is acting in, e.g.
 * Noctua.group('organization', 'acme', { name: 'Acme Corp' }). Persisted on
 * the host origin (the capture bundle loads async and replays this at boot —
 * a pre-boot dispatch alone would be lost) and mirrored into telemetry so
 * every subsequent event carries the account stamp.
 */
function group(groupType, groupKey, traits) {
  if (typeof groupType !== 'string' || !groupType.trim()) return
  if (typeof groupKey !== 'string' && typeof groupKey !== 'number') return
  const entry = {
    key: String(groupKey),
    ...(traits && typeof traits === 'object' ? { traits } : {}),
  }
  try {
    const stored = readStoredGroups()
    stored[groupType.trim()] = entry
    localStorage.setItem(GROUP_STORAGE_KEY, JSON.stringify(stored))
  } catch {
    // Storage unavailable — the group just won't persist across sessions.
  }
  try {
    window.dispatchEvent(
      new CustomEvent('noctua:group', {
        detail: { groupType: groupType.trim(), groupKey: String(groupKey), traits },
      })
    )
  } catch {
    // CustomEvent unavailable — capture just misses this group call.
  }
}

function clearIdentity() {
  try {
    localStorage.removeItem(STORAGE_KEY)
    localStorage.removeItem(GROUP_STORAGE_KEY)
  } catch {
    // Ignore, same as identify().
  }
  try {
    window.dispatchEvent(new CustomEvent('noctua:reset'))
  } catch {
    // Ignore, same as identify().
  }
}

function readStoredIdentity() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    // Corrupted/legacy values that parse to a non-object (a bare string would
    // spread one key per character into the widget context) read as absent.
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null
  } catch {
    return null
  }
}

/** { [groupType]: { key, traits? } } — the shape group() persists. */
function readStoredGroups() {
  try {
    const raw = localStorage.getItem(GROUP_STORAGE_KEY)
    const parsed = raw ? JSON.parse(raw) : null
    return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : {}
  } catch {
    return {}
  }
}

// Capture host-page environment (page, locale, device). Runs in the parent
// window so it reflects the host site, not the widget iframe. Best-effort:
// anything unavailable is simply omitted.
function collectEnvironment() {
  const env = {}
  try {
    env.url = window.location.href
    env.path = window.location.pathname
    env.title = document.title
    env.referrer = document.referrer || null
    env.userAgent = navigator.userAgent
    env.language = navigator.language
    if (navigator.languages?.length) env.languages = [...navigator.languages]
    env.timezone = Intl.DateTimeFormat().resolvedOptions().timeZone
    if (window.screen) {
      env.screen = { width: window.screen.width, height: window.screen.height }
    }
    env.viewport = { width: window.innerWidth, height: window.innerHeight }
  } catch {
    // Partial environment is fine.
  }
  return env
}

/**
 * Identity/context precedence, lowest to highest:
 * environment < window.noctuaSettings < identify() < startFeedback({ context }).
 * Settings are the static site-wide defaults; identify() is the app telling us
 * who the visitor is; an explicit startFeedback() context wins over everything.
 */
function buildContext(config) {
  const settings = window.noctuaSettings || {}
  const userId = settings.userId ?? settings.user_id
  const appVersion = settings.appVersion ?? settings.version
  // Telemetry identity (when the capture bundle is running) rides along so a
  // feedback conversation can be joined to this device's product usage.
  const capture = window.__noctuaCaptureState
  return {
    ...collectEnvironment(),
    ...(capture?.distinctId && { telemetryDistinctId: capture.distinctId }),
    ...(capture?.sessionId && { telemetrySessionId: capture.sessionId }),
    ...(capture?.groups &&
      Object.keys(capture.groups).length > 0 && { telemetryGroups: capture.groups }),
    ...(appVersion != null && { appVersion: String(appVersion) }),
    ...(userId != null && { id: String(userId) }),
    ...(settings.name != null && { name: settings.name }),
    ...(settings.email != null && { email: settings.email }),
    ...readStoredIdentity(),
    ...(config.context || {}),
  }
}

function startFeedback(config = {}) {
  const settings = window.noctuaSettings || {}
  const publicKey = config.publicKey || settings.publicKey
  if (!publicKey) {
    throw new Error('Noctua: window.noctuaSettings.publicKey is required before startFeedback()')
  }
  const apiBase = config.apiBase || settings.apiBase || DEFAULT_API_BASE
  if (!apiBase) {
    throw new Error('Noctua: window.noctuaSettings.apiBase is required before startFeedback()')
  }

  // Only one session at a time; restarting replaces the current one.
  if (iframe) destroy()

  const widgetUrl = config.widgetUrl || settings.widgetUrl || DEFAULT_WIDGET_URL
  widgetOrigin = new URL(widgetUrl, window.location.href).origin
  initPayload = { publicKey, apiBase, context: buildContext(config) }

  ensureMessageListener()
  iframe = createIframe(widgetUrl)
  whenBodyReady(() => {
    if (!iframe) return // destroyed before the DOM was ready
    lockScroll()
    bindEscape()
    document.body.appendChild(iframe)
  })
}

function destroy() {
  if (escapeHandler) {
    document.removeEventListener('keydown', escapeHandler)
    escapeHandler = null
  }
  unlockScroll()
  if (iframe) {
    iframe.remove()
    iframe = null
  }
  widgetOrigin = null
  initPayload = null
  initialized = false
}

function createIframe(url) {
  const el = document.createElement('iframe')
  el.id = IFRAME_ID
  el.title = 'Noctua feedback'
  el.src = url
  // position:fixed + inset:0 tracks the visual viewport, including mobile
  // URL-bar resizes, without any vh/dvh gymnastics. color-scheme:normal stops
  // dark-mode browsers from forcing an opaque canvas behind the transparent
  // cross-origin document.
  el.style.cssText = [
    'position: fixed',
    'inset: 0',
    'width: 100%',
    'height: 100%',
    'border: none',
    'z-index: 2147483000',
    'background: transparent',
    'color-scheme: normal',
  ].join('; ')
  return el
}

function ensureMessageListener() {
  if (messageListenerBound) return
  messageListenerBound = true
  window.addEventListener('message', (event) => {
    const msg = event.data
    if (!msg || typeof msg !== 'object') return
    if (iframe && event.source === iframe.contentWindow && event.origin === widgetOrigin) {
      handleWidgetMessage(msg)
    } else if (
      launcherFrame &&
      event.source === launcherFrame.contentWindow &&
      event.origin === launcherOrigin
    ) {
      handleLauncherMessage(msg)
    }
  })
}

function handleWidgetMessage(msg) {
  if (msg.type === 'noctua:ready') {
    // Init exactly once per session, no matter how often ready is signalled.
    if (initialized) return
    initialized = true
    iframe.contentWindow.postMessage({ type: 'noctua:init', data: initPayload }, widgetOrigin)
  } else if (msg.type === 'noctua:close') {
    destroy()
  }
}

function handleLauncherMessage(msg) {
  if (msg.type === 'noctua:launcher-ready') {
    if (launcherInitialized) return
    launcherInitialized = true
    const settings = window.noctuaSettings || {}
    launcherFrame.contentWindow.postMessage(
      {
        type: 'noctua:launcher-init',
        data: { publicKey: settings.publicKey, apiBase: settings.apiBase || DEFAULT_API_BASE },
      },
      launcherOrigin,
    )
  } else if (msg.type === 'noctua:launcher-size') {
    placeLauncher(msg.data)
  } else if (msg.type === 'noctua:open') {
    startFeedback()
  }
}

/**
 * Boot the Noctua-provided launcher button. Runs once at script evaluation:
 * the launcher is settings-driven (publicKey + apiBase must be in
 * window.noctuaSettings before the script loads), and whether anything shows
 * is decided inside the launcher iframe after it fetches the channel config —
 * the frame stays 0x0 and invisible until the launcher asks for space.
 */
function bootLauncher() {
  const settings = window.noctuaSettings || {}
  if (settings.hideLauncher) return
  const apiBase = settings.apiBase || DEFAULT_API_BASE
  if (!settings.publicKey || !apiBase) return
  if (launcherFrame) return

  // A frame from an earlier instance of this script (hot reload, double
  // include) would be orphaned — replace it.
  document.getElementById(LAUNCHER_IFRAME_ID)?.remove()

  const launcherUrl = settings.launcherUrl || DEFAULT_LAUNCHER_URL
  launcherOrigin = new URL(launcherUrl, window.location.href).origin
  ensureMessageListener()

  const el = document.createElement('iframe')
  el.id = LAUNCHER_IFRAME_ID
  el.title = 'Noctua feedback launcher'
  el.src = launcherUrl
  // Sits just under the conversation overlay (z-index 2147483000), so an open
  // conversation covers it. Sized/anchored later by placeLauncher().
  el.style.cssText = [
    'position: fixed',
    'width: 0',
    'height: 0',
    'border: none',
    'z-index: 2147482999',
    'background: transparent',
    'color-scheme: normal',
  ].join('; ')
  launcherFrame = el
  whenBodyReady(() => {
    if (launcherFrame) document.body.appendChild(launcherFrame)
  })
}

/**
 * Apply the frame placement the launcher computed. `anchors` carries px
 * insets for the anchored edges (0 = flush tab edge) and 'center' on left/top
 * for middle positions — all geometry knowledge (offsets, shadow padding,
 * attachment) lives in the launcher; the loader just applies CSS.
 */
function placeLauncher(data) {
  if (!launcherFrame || !data) return
  const width = Number(data.width)
  const height = Number(data.height)
  if (!Number.isFinite(width) || !Number.isFinite(height) || width <= 0 || height <= 0) return
  const anchors = data.anchors || {}
  const st = launcherFrame.style
  st.width = `${width}px`
  st.height = `${height}px`
  for (const edge of ['top', 'bottom', 'left', 'right']) {
    const value = anchors[edge]
    if (value === 'center' && (edge === 'top' || edge === 'left')) {
      st[edge] = '50%'
    } else if (Number.isFinite(Number(value)) && value !== null && value !== '') {
      st[edge] = `${Number(value)}px`
    } else {
      st[edge] = ''
    }
  }
  const tx = anchors.left === 'center' ? '-50%' : '0'
  const ty = anchors.top === 'center' ? '-50%' : '0'
  st.transform = tx === '0' && ty === '0' ? '' : `translate(${tx}, ${ty})`
}

function bindEscape() {
  // Stacked whenBodyReady callbacks (session restarted before the body
  // existed) must not each bind a handler: one per session, ever.
  if (escapeHandler) return
  escapeHandler = (event) => {
    if (event.key !== 'Escape') return
    event.preventDefault()
    // The widget decides whether escape closes immediately or asks to confirm.
    if (initialized && iframe?.contentWindow) {
      iframe.contentWindow.postMessage({ type: 'noctua:close-request' }, widgetOrigin)
    }
  }
  document.addEventListener('keydown', escapeHandler)
}

function whenBodyReady(fn) {
  if (document.body) {
    fn()
  } else {
    document.addEventListener('DOMContentLoaded', fn, { once: true })
  }
}

function lockScroll() {
  if (scrollState) return
  const gap = window.innerWidth - document.documentElement.clientWidth
  scrollState = {
    overflow: document.documentElement.style.overflow,
    paddingRight: document.body.style.paddingRight,
  }
  document.documentElement.style.overflow = 'hidden'
  // Keep layout stable where a scrollbar just disappeared.
  if (gap > 0) document.body.style.paddingRight = `${gap}px`
}

function unlockScroll() {
  if (!scrollState) return
  document.documentElement.style.overflow = scrollState.overflow
  document.body.style.paddingRight = scrollState.paddingRight
  scrollState = null
}

/**
 * Boot product-telemetry capture. Runs once at script evaluation, like the
 * launcher: settings-driven (publicKey + apiBase must be present), and the
 * decision to actually capture lives server-side in the channel's telemetry
 * config — one cacheable GET, then the capture bundle (deployed next to
 * this loader) is injected only when enabled. Pages with telemetry off pay
 * one cached fetch and zero extra script bytes. Set
 * noctuaSettings.disableTelemetry to skip even the config fetch (e.g. while
 * awaiting cookie consent), then call Noctua.enableTelemetry() to proceed.
 */
function bootCapture() {
  const settings = window.noctuaSettings || {}
  if (settings.disableTelemetry) return
  const apiBase = settings.apiBase || DEFAULT_API_BASE
  if (!settings.publicKey || !apiBase) return
  if (window.__noctuaCapture || window.__noctuaCaptureBooted) return

  const configUrl =
    `${apiBase.replace(/\/$/, '')}/api/v1/telemetry/config` +
    `?publicKey=${encodeURIComponent(settings.publicKey)}`
  fetch(configUrl)
    .then((response) => (response.ok ? response.json() : null))
    .then((config) => {
      if (!config?.telemetry?.enabled) return
      if (window.__noctuaCapture || window.__noctuaCaptureBooted) return
      window.__noctuaCapture = {
        publicKey: settings.publicKey,
        apiBase,
        telemetry: config.telemetry,
      }
      const script = document.createElement('script')
      script.src = new URL('capture.js', loaderSrc || document.baseURI).href
      script.async = true
      document.head.appendChild(script)
    })
    .catch(() => {
      // Telemetry is best-effort; the page must never notice.
    })
}

function enableTelemetry() {
  if (window.noctuaSettings) window.noctuaSettings.disableTelemetry = false
  bootCapture()
}

// Loader version, statically injected at build time from package.json (see
// vite.config.loader.js). Null in dev/tests unless the env is stubbed.
const SDK_VERSION = import.meta.env.VITE_NOCTUA_VERSION || null

const HB_STORAGE_KEY = 'noctua-hb'

/**
 * Install-liveness heartbeat: one uncacheable POST per browser session telling
 * the dashboard "the snippet is on this page and running". It exists because
 * the other signals can all go quiet on a healthy install — config fetches
 * get CDN-cached for hours, capture beacons are consent-gated — and it is
 * deliberately NOT gated on disableTelemetry: it carries no user data (public
 * key, loader version, and the page origin every request exposes anyway).
 *
 * Once per session, keyed by publicKey so a key rotation re-announces.
 * sendBeacon so an immediate navigation can't cancel it; fetch(keepalive)
 * where sendBeacon is unavailable. The guard is set even if the send fails —
 * liveness is best-effort and the opportunistic signals back it up.
 */
function sendHeartbeat() {
  const settings = window.noctuaSettings || {}
  const apiBase = settings.apiBase || DEFAULT_API_BASE
  if (!settings.publicKey || !apiBase) return
  try {
    if (sessionStorage.getItem(HB_STORAGE_KEY) === settings.publicKey) return
  } catch {
    // Storage unavailable — send every load; the server debounces.
  }
  const url =
    `${apiBase.replace(/\/$/, '')}/api/v1/widget/hb` +
    `?publicKey=${encodeURIComponent(settings.publicKey)}` +
    (SDK_VERSION ? `&v=${encodeURIComponent(SDK_VERSION)}` : '')
  let sent = false
  try {
    if (navigator.sendBeacon) sent = navigator.sendBeacon(url)
  } catch {
    // Blocked beacon — the fetch fallback covers it.
  }
  if (!sent) {
    try {
      fetch(url, { method: 'POST', keepalive: true }).catch(() => {})
    } catch {
      // Liveness is best-effort; the page must never notice.
    }
  }
  try {
    sessionStorage.setItem(HB_STORAGE_KEY, settings.publicKey)
  } catch {
    // Same as the read above.
  }
}

const Noctua = { identify, group, clearIdentity, startFeedback, destroy, enableTelemetry }

// Drain calls queued by the async embed snippet (window.Noctua = { q: [...] })
// so integrators can call Noctua.* before this script finishes loading.
const queued = window.Noctua && Array.isArray(window.Noctua.q) ? window.Noctua.q.slice() : []
window.Noctua = Noctua
// Entries come from hand-rolled snippets: one malformed entry must not abort
// the drain — or the launcher/capture/heartbeat boot below it.
for (const entry of queued) {
  try {
    const [method, ...args] = Array.isArray(entry) ? entry : []
    if (typeof Noctua[method] === 'function') Noctua[method](...args)
  } catch (err) {
    console.error('Noctua: queued call failed', entry, err)
  }
}

bootLauncher()
bootCapture()
sendHeartbeat()

export default Noctua
